// frontend/src/pages/Signup.jsx

import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "../axiosConfig";

export default function Signup() {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
    contact: "",
    country: "India",
    password: "",
  });
  const [msg, setMsg] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPw, setShowPw] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === "contact") {
      const v = value.replace(/[^\d+\-\s()]/g, "");
      setForm((f) => ({ ...f, contact: v }));
      return;
    }
    setForm((f) => ({ ...f, [name]: value }));
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMsg("");
    try {
      // 1) email signup (unchanged)
      await axios.post("/signup", {
        ...form,
        contact: String(form.contact || "").replace(/[^\d+\-\s()]/g, ""),
      });

      // 2) kick off mobile OTP (non-blocking if fails)
      let mobileInfo = null;
      try {
        const { data } = await axios.post("/send-mobile-otp", { email: form.email.trim() });
        // expect: { ok: true, requestId, mobileE164 }
        mobileInfo = data;
        if (data?.requestId) {
          localStorage.setItem("mobileRequestId", data.requestId);
        }
        if (data?.mobileE164) {
          localStorage.setItem("mobileE164", data.mobileE164);
        }
      } catch (e) {
        // soft warn only
        console.warn("send-mobile-otp failed:", e?.response?.data || e?.message);
      }

      // 3) carry context to verify page
      localStorage.setItem("signupEmail", (form.email || "").trim());
      localStorage.setItem("userName", (form.firstName || "").trim());
      setMsg(
        mobileInfo?.requestId
          ? "Signup successful! Email & SMS OTP sent. Redirecting…"
          : "Signup successful! Email OTP sent. Redirecting…"
      );
      setTimeout(() => navigate("/verify-otp"), 1000);
    } catch (err) {
      const errorMsg = err.response?.data?.error || "Signup failed. Please try again.";
      setMsg(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-slate-900 px-3 sm:px-4 py-6">
      <style>{`
        .dark input:-webkit-autofill,
        .dark input:-webkit-autofill:focus,
        .dark input:-webkit-autofill:hover {
          -webkit-text-fill-color: #e5e7eb;
          transition: background-color 5000s ease-in-out 0s;
          caret-color: #e5e7eb;
        }
        .dark input:-webkit-autofill::first-line { color: #e5e7eb; }
      `}</style>

      <motion.div
        className="w-full max-w-sm sm:max-w-md md:max-w-lg bg-white dark:bg-slate-900/95 border border-slate-200 dark:border-slate-700 rounded-2xl shadow-lg p-4 sm:p-6 md:p-8"
        initial={{ opacity: 0, y: 18 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35 }}
      >
        <h2 className="text-2xl sm:text-3xl font-bold text-center text-indigo-700 dark:text-indigo-300 mb-1.5 sm:mb-2">
          Create Your Account
        </h2>
        <p className="text-xs sm:text-sm text-center text-gray-700 dark:text-gray-300 mb-5 sm:mb-6">
          Start trading in minutes.
        </p>

        {msg && (
          <div
            className={`text-center text-xs sm:text-sm p-2.5 sm:p-3 mb-3 sm:mb-4 rounded-md border
              ${
                /successful|otp/i.test(msg)
                  ? "bg-green-50 text-green-700 border-green-200 dark:bg-green-900/30 dark:text-green-300 dark:border-green-800/40"
                  : "bg-red-50 text-red-700 border-red-200 dark:bg-red-900/30 dark:text-red-300 dark:border-red-800/40"
              }`}
            role="alert"
            aria-live="polite"
          >
            {msg}
          </div>
        )}

        <form onSubmit={handleSignup} className="space-y-4 sm:space-y-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
            <Input label="First Name" name="firstName" value={form.firstName} onChange={handleChange} placeholder="First Name" required autoComplete="given-name" />
            <Input label="Last Name" name="lastName" value={form.lastName} onChange={handleChange} placeholder="Last Name" required autoComplete="family-name" />
          </div>

          <Input label="Email" name="email" type="email" value={form.email} onChange={handleChange} placeholder="you@example.com" required autoComplete="email" inputMode="email" />

          <Input label="Contact Number" name="contact" type="tel" inputMode="tel" pattern="^[0-9+\-\s()]{7,}$" value={form.contact} onChange={handleChange} placeholder="+91 XXXXXXXXXX" required autoComplete="tel" />

          <Input label="Country" name="country" value={form.country} onChange={handleChange} placeholder="Country" autoComplete="country-name" />

          <div>
            <label className="block text-gray-800 dark:text-gray-200 text-xs sm:text-sm mb-1">Password</label>
            <div className="relative">
              <input
                name="password"
                type={showPw ? "text" : "password"}
                placeholder="••••••••"
                value={form.password}
                onChange={handleChange}
                required
                autoComplete="new-password"
                className="w-full text-sm sm:text-base px-3 sm:px-4 py-2.5 sm:py-3 border border-gray-300 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800/80 text-gray-900 dark:text-slate-100 placeholder-gray-500 outline-none focus:ring-2 focus:ring-indigo-300 dark:focus:ring-indigo-500 focus:border-indigo-400 dark:focus:border-indigo-500 transition"
              />
              <button
                type="button"
                onClick={() => setShowPw((s) => !s)}
                className="absolute inset-y-0 right-0 px-3 sm:px-4 text-xs sm:text-sm text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-gray-200"
                aria-label={showPw ? "Hide password" : "Show password"}
              >
                {showPw ? "Hide" : "Show"}
              </button>
            </div>
            <p className="mt-1 text-[11px] sm:text-xs text-gray-600 dark:text-gray-400">Use 8+ characters with a mix of letters & numbers.</p>
          </div>

          <button
            type="submit"
            disabled={loading}
            className={`w-full text-white text-sm sm:text-base py-2.5 sm:py-3 rounded-lg transition disabled:opacity-60 ${
              loading ? "bg-indigo-400 dark:bg-indigo-500 cursor-not-allowed" : "bg-indigo-700 hover:bg-indigo-800 dark:bg-indigo-600 dark:hover:bg-indigo-700"
            }`}
          >
            {loading ? "Signing up..." : "Sign Up"}
          </button>

          <p className="text-center text-xs sm:text-sm text-gray-700 dark:text-gray-300">
            Already have an account?{" "}
            <Link to="/login" className="text-indigo-700 dark:text-indigo-400 hover:underline font-medium">
              Log in
            </Link>
          </p>
        </form>
      </motion.div>
    </div>
  );
}

function Input({ label, name, type = "text", value, onChange, placeholder, required, inputMode, pattern, autoComplete }) {
  return (
    <div>
      <label className="block text-gray-800 dark:text-gray-200 text-xs sm:text-sm mb-1">{label}</label>
      <input
        name={name}
        type={type}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        required={required}
        inputMode={inputMode}
        pattern={pattern}
        autoComplete={autoComplete}
        className="w-full text-sm sm:text-base px-3 sm:px-4 py-2.5 sm:py-3 border border-gray-300 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800/80 text-gray-900 dark:text-slate-100 placeholder-gray-500 outline-none focus:ring-2 focus:ring-indigo-300 dark:focus:ring-indigo-500 focus:border-indigo-400 dark:focus:border-indigo-500 transition"
      />
    </div>
  );
}
